<template>
  <v-app>
    <mainMenu></mainMenu>
    <v-container fluid>
      <v-alert v-if="loadError.length" type="error" dense class="mb-2">{{ loadError.join(" / ") }}</v-alert>
      <div class="d-flex align-center mb-2">
        <h2 class="mr-4">รถใหญ่ต่างจังหวัด</h2>
        <span class="caption">ขั้นต่ำ ฿{{ money(cfg.minValue) }} (มาตรฐาน ฿{{ money(cfg.standardValue) }}) · ความจุรถ {{ cbm(cfg.capacityCbm) }} CBM</span>
        <v-spacer />
        <v-btn small outlined class="mr-2" @click="planSettings = true">ตั้งค่าแผนเส้นทาง</v-btn>
        <v-btn v-if="isAdmin" small outlined @click="configDialog = true">ตั้งค่าเกณฑ์</v-btn>
      </div>

      <!-- Week strip: usual departures and which trucks are away. -->
      <v-simple-table dense class="mb-4">
        <thead><tr><th>วัน</th><th v-for="d in stripDays" :key="d.date">{{ d.label }}</th></tr></thead>
        <tbody>
          <tr><td>ออกปกติ</td><td v-for="d in stripDays" :key="d.date">{{ d.usual }} คัน</td></tr>
          <tr><td>รถว่าง</td><td v-for="d in stripDays" :key="d.date">{{ d.free.join(', ') || '-' }}</td></tr>
        </tbody>
      </v-simple-table>

      <!-- Trips -->
      <div class="d-flex align-center flex-wrap" style="gap: 8px">
        <h3>รอบที่จัดแล้ว</h3>
        <span v-if="liveTrips.length" class="caption">{{ liveTrips.length }} รอบ</span>
        <v-spacer />
        <template v-if="liveTrips.length">
          <v-btn x-small text @click="setAllTrips(true)">ขยายทั้งหมด</v-btn>
          <v-btn x-small text @click="setAllTrips(false)">ย่อทั้งหมด</v-btn>
        </template>
      </div>
      <div v-if="!liveTrips.length" class="body-2 grey--text mb-2">
        ยังไม่มีรอบที่จัดแล้ว — กด จัดรอบ ที่กลุ่มด้านล่างเมื่อออเดอร์ครบเกณฑ์
      </div>
      <!-- Each card's header (dates, truck, status, totals) and its buttons always
           show; the order list opens on a click. Trips leaving within
           U.TRIP_OPEN_DAYS open by themselves -- they are the ones being worked on. -->
      <v-card v-for="trip in liveTrips" :key="trip.id" outlined class="mb-2 pa-3">
        <div class="d-flex flex-wrap align-center trip-head" style="gap: 8px" @click="toggleTrip(trip)">
          <v-icon small>{{ isTripOpen(trip) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          <strong>{{ trip.startDate }} → {{ trip.returnDate }}</strong>
          <v-chip small>{{ trip.regNum }}</v-chip>
          <v-chip small v-for="g in trip.groups" :key="g">{{ U.groupLabel(g) }}</v-chip>
          <v-chip small :color="trip.status === 'sent' ? 'green' : 'blue'" text-color="white">
            {{ trip.status === 'sent' ? 'ส่งต่อจัดรถแล้ว' : 'จัดรอบแล้ว' }}</v-chip>
          <v-chip v-if="trip.belowMin" small color="red" text-color="white">ต่ำกว่าเกณฑ์</v-chip>
          <v-chip v-if="trip.transNo" small outlined>ใบจัดรถ {{ trip.transNo }} · {{ trip.driver }}</v-chip>
          <v-chip v-if="trip.sendState === 'pending'" small color="red" text-color="white">ส่งไม่ครบ</v-chip>
          <v-chip v-if="U.overdueTrip(trip, today)" small color="orange darken-2" text-color="white">
            ⚠ {{ U.overdueTrip(trip, today) === 'NOT_CLOSED' ? 'เลยวันกลับแล้ว — กด ปิดรอบ เมื่อรถกลับ' : 'เลยวันกลับแล้ว — ยังไม่ได้ทำเครื่องหมายว่าส่งแล้ว' }}</v-chip>
          <v-chip v-if="trip.plan" small outlined :color="P.planIsCurrent(trip) ? 'green' : 'orange'">
            {{ P.planIsCurrent(trip) ? 'มีแผนเส้นทาง' : 'ออเดอร์เปลี่ยนหลังบันทึกแผน — เปิด วางแผนเส้นทาง เพื่อปรับ' }}</v-chip>
          <span class="caption">{{ ordersOf(trip).length }} ออเดอร์ · {{ U.totals(ordersOf(trip), cfg).count }} ชิ้น · {{ cbm(trip.volume) }} / {{ cbm(cfg.capacityCbm) }} CBM · ฿{{ money(trip.value) }}</span>
        </div>
        <v-simple-table v-if="isTripOpen(trip)" dense class="mt-2">
          <tbody>
            <tr v-for="o in ordersOf(trip)" :key="o.id">
              <td>{{ refOf(o) }}</td><td>{{ o.name }}</td><td>{{ o.telephone }}</td><td>{{ o.upProvince }}</td>
              <td class="text-no-wrap">{{ o.date && o.date !== trip.startDate ? 'ส่ง ' + o.date : '' }}</td>
              <td class="text-no-wrap">{{ U.orderFigures(o, cfg).count }} ชิ้น · {{ cbm(U.orderFigures(o, cfg).volume) }} CBM</td>
              <td>
                <div class="d-flex align-center">
                  <span class="text-no-wrap mr-2">การติดตั้ง:</span>
                  <v-select class="install-mode" :value="o.installMode" :items="modeItems" dense hide-details style="max-width: 120px"
                    @change="m => act(() => US.setInstallMode(o.id, { mode: m, user, role }))" />
                </div>
              </td>
              <td class="text-no-wrap">
                <!-- A sent trip inside its window: the same three, each also taking the
                     order off the dispatch sheet and out of SAP (takeOffSent). Past the
                     window, only an order with no row on the sheet (rowlessPastWindow). -->
                <template v-if="showTakeOff(trip, o)">
                  <v-btn x-small text :disabled="!!takingOff" @click="postpone(o)">เลื่อนไปรอบถัดไป</v-btn>
                  <v-btn x-small text color="error" :disabled="!!takingOff" @click="trip.status === 'sent' ? takeOffSent(o, trip, 'pool') : removeFromTrip(o, trip)">นำออกจากรอบ</v-btn>
                  <!-- A fixed trip's ยกเลิกออเดอร์ refuses a pending request: it is decided on คำขอรอยืนยัน. -->
                  <v-btn x-small text color="error" :disabled="!!takingOff || (trip.status === 'fixed' && o.proposalStatus === 'pending')"
                    @click="trip.status === 'sent' ? takeOffSent(o, trip, 'cancel') : cancelOrder(o)">ยกเลิกออเดอร์</v-btn>
                </template>
                <span v-else-if="lastOnSent(trip)" class="caption">ออเดอร์สุดท้ายของรอบ — ใช้ ยกเลิกการส่ง หรือ ยกเลิกรอบ</span>
                <v-chip v-if="hasCancelRequest(o)" x-small color="orange" text-color="white"
                  :title="trip.status === 'fixed' ? 'ตัดสินคำขอที่ คำขอรอยืนยัน' : ''">มีคำขอยกเลิก</v-chip>
                <span v-if="hasCancelRequest(o) && trip.status === 'fixed'" class="caption ml-1">ตัดสินคำขอที่ คำขอรอยืนยัน</span>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div class="d-flex flex-wrap mt-2" style="gap: 6px">
          <v-btn v-if="trip.status === 'fixed' || sentReplan(trip)" small text color="primary" @click="openPlan(trip)">วางแผนเส้นทาง</v-btn>
          <v-btn v-if="trip.status === 'sent' && trip.plan && !sentReplan(trip)" small text color="primary" @click="openPlan(trip)">ดูเส้นทาง</v-btn>
          <v-btn v-if="trip.status === 'fixed' && trip.plan" small text color="error" @click="clearPlan(trip)">ยกเลิกแผน</v-btn>
          <v-btn v-if="editable(trip)" small text @click="openAdd(trip)">+ เพิ่มออเดอร์</v-btn>
          <v-btn small text @click="editReturn(trip)">แก้วันกลับ</v-btn>
          <v-btn small text @click="copyHandover(trip)">คัดลอกรายการให้จัดรถ</v-btn>
          <v-btn v-if="trip.status === 'fixed'" small color="primary" :disabled="!P.planIsCurrent(trip)" @click="openSend(trip)">ส่งเข้าจัดรถ</v-btn>
          <span v-if="trip.status === 'fixed' && !P.planIsCurrent(trip)" class="caption align-self-center">บันทึกแผนก่อน</span>
          <v-btn v-if="trip.sendState === 'pending'" small text color="red" @click="openSend(trip)">ลองอีกครั้ง</v-btn>
          <span v-if="trip.sendState === 'pending' && sentReplan(trip)" class="caption align-self-center">บันทึกแผนก่อน</span>
          <v-btn v-if="trip.transNo" small text :href="'#/reports/delivery?transNo=' + trip.transNo" target="_blank">พิมพ์ใบขนส่ง</v-btn>
          <v-btn v-if="trip.transNo" small text color="error" @click="unsendTrip(trip)">ยกเลิกการส่ง</v-btn>
          <v-btn v-if="trip.status === 'fixed'" small text @click="act(() => US.markSent(trip.id, { user }))">ทำเครื่องหมายว่าส่งแล้ว (คีย์เอง)</v-btn>
          <v-btn v-if="trip.status === 'sent' && !trip.transNo" small text @click="act(() => US.unmarkSent(trip.id, { user }))">ย้อนสถานะ</v-btn>
          <v-btn v-if="trip.status === 'sent'" small text @click="close(trip)">ปิดรอบ</v-btn>
          <v-btn v-if="trip.status === 'fixed'" small text color="error" @click="cancelTrip(trip)">ยกเลิกรอบ</v-btn>
        </div>
      </v-card>

      <!-- Group board -->
      <div class="d-flex align-center flex-wrap mt-4" style="gap: 8px">
        <h3>ออเดอร์รอจัดรอบ</h3>
        <span class="caption">{{ pooledCount }} ออเดอร์</span>
        <v-spacer />
        <v-btn x-small text @click="setAllGroups(true)">ขยายทั้งหมด</v-btn>
        <v-btn x-small text @click="setAllGroups(false)">ย่อทั้งหมด</v-btn>
      </div>
      <v-row>
        <v-col v-for="card in cards" :key="card.key" cols="12" md="6" lg="4" class="d-flex">
          <!-- Fills its column, so every card in a row is as tall as the tallest, and
               จัดรอบ is pushed to the bottom (mt-auto) so the buttons line up. -->
          <v-card outlined class="pa-3 group-card">
            <div class="d-flex align-center">
              <strong>{{ card.label }}</strong><v-spacer />
              <v-chip v-if="card.ready === 'own'" small color="green" text-color="white">พร้อมจัดรอบ</v-chip>
              <v-chip v-else-if="card.ready === 'shared'" small color="amber">พร้อมถ้ารวมออเดอร์ร่วม</v-chip>
            </div>
            <!-- What the group covers: the label alone does not say which run ชุมพร is on. -->
            <group-provinces :group-key="card.key" />
            <!-- Volume against the truck, in CBM, plus the SO's real item count. Never
                 volume / 0.14 shown as ชิ้น: "0.9 / 50" read as a fraction of a kiosk. -->
            <div class="caption mt-1">
              ปริมาตร {{ cbm(card.own.volume) }}<span v-if="card.shared.orders"> (+{{ cbm(card.shared.volume) }} ร่วม)</span>
              / {{ cbm(cfg.capacityCbm) }} CBM · {{ card.own.count }}<span v-if="card.shared.orders"> (+{{ card.shared.count }})</span> ชิ้น
            </div>
            <v-progress-linear :value="pct(card.own.volume, cfg.capacityCbm)" :buffer-value="pct(card.own.volume + card.shared.volume, cfg.capacityCbm)" height="8" />
            <div class="caption mt-1">฿{{ money(card.own.value) }}<span v-if="card.shared.orders"> (+{{ money(card.shared.value) }} ร่วม)</span> / ฿{{ money(cfg.minValue) }}</div>
            <v-progress-linear :value="pct(card.own.value, cfg.minValue)" :buffer-value="pct(card.own.value + card.shared.value, cfg.minValue)" color="green" height="8" />
            <!-- The order list opens on a click; a ready group's opens by itself
                 (U.groupOpenByDefault). The bars and จัดรอบ always show. -->
            <div v-if="card.pool.length" class="caption mt-2 trip-head" @click="toggleGroup(card)">
              <v-icon small>{{ isGroupOpen(card) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              {{ card.pool.length }} ออเดอร์
            </div>
            <!-- Two lines per order, not a seven-column table: a third of the screen
                 squeezed the name column until a customer's name wrapped letter by
                 letter. Name and ฿ (what is weighed against the minimum) on top, the
                 rest underneath, wrapping freely. -->
            <div v-if="card.pool.length && isGroupOpen(card)" class="mt-1">
              <div v-for="o in card.pool" :key="o.id" class="pool-row">
                <div class="d-flex align-baseline" style="gap: 8px">
                  <span class="pool-name" :title="o.name">{{ o.name }}</span>
                  <v-spacer />
                  <span class="text-no-wrap">฿{{ money(o.orderValue) }}</span>
                </div>
                <!-- Two fixed lines -- the order, then how it waits -- of whole items
                     (.pool-item), so a narrow card breaks only at a " · " and never
                     between การติดตั้ง: and its value or a date and its time. -->
                <div class="caption grey--text text--darken-1 pool-line">
                  <span v-if="o.soNo" class="pool-item">SO <router-link :to="{ name: 'bookingDate', query: { q: o.soNo } }"
                    title="เปิดในจองวันส่งสินค้า (ยกเลิก / การติดตั้ง)">{{ o.soNo }}</router-link><template v-if="o.rtNo"> + RT {{ o.rtNo }}</template></span><span
                    v-else-if="o.rtNo" class="pool-item">RT <router-link :to="{ name: 'bookingDate', query: { q: o.rtNo } }"
                    title="เปิดในจองวันส่งสินค้า (ยกเลิก / การติดตั้ง)">{{ o.rtNo }}</router-link> (รับคืน)</span><span v-if="o.soNo || o.rtNo"> · </span><span class="pool-item">{{ o.upProvince }}<template v-if="o.upGroup.length > 1"> (ร่วม)</template></span>
                  · <span class="pool-item">{{ U.orderFigures(o, cfg).count }} ชิ้น</span>
                  · <span class="pool-item">{{ cbm(U.orderFigures(o, cfg).volume) }} CBM</span>
                </div>
                <div class="caption grey--text text--darken-1 pool-line">
                  <span class="pool-item">ตั้งแต่ {{ U.weekLabel(o.earliestWeek) }}</span>
                  · <span class="pool-item">การติดตั้ง: {{ modeText(o.installMode) }}</span>
                  <!-- First come, first served within a week: the time each order was
                       booked is what that order is, and the SO numbers do not show it. -->
                  <span v-if="o.createdAt"> · <span class="pool-item">{{ U.bookedLabel(o.createdAt) }}</span></span>
                </div>
              </div>
            </div>
            <div class="mt-auto pt-2 d-flex flex-wrap" style="gap: 6px">
              <v-btn small color="primary" :disabled="!card.pool.length" @click="openFix(card.key)">จัดรอบ</v-btn>
              <!-- Re-reads GetSo / GetProdList for the group's WAITING orders: an order
                   keeps the figures it was booked with, so a corrected item master or
                   an edited SO reaches it only through this. -->
              <v-btn v-if="card.pool.length" small outlined :loading="!!refreshing[card.key]"
                     :disabled="anyRefreshing" @click="refreshGroup(card)">อัปเดตจาก SAP</v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <shop-closed-days :rows="shopRows" :orders="orders" :user="user" />
    </v-container>

    <!-- Fix dialog -->
    <v-dialog v-model="fix.open" max-width="900">
      <v-card>
        <v-card-title>จัดรอบรถใหญ่</v-card-title>
        <v-card-text>
          <v-select v-model="fix.groups" :items="groupItems" multiple label="กลุ่มเส้นทาง" dense outlined @change="refreshFix" />
          <v-text-field v-model="fix.startDate" type="date" label="วันออกรถ (โหลดสินค้าวันก่อนหน้า)" dense outlined @change="refreshFix" />
          <v-text-field v-model="fix.returnDate" type="date" :hint="'ตามตาราง ' + tableDays + ' วัน'" persistent-hint label="วันกลับ" dense outlined />
          <v-select v-model="fix.regNum" :items="freePlates" label="รถ" dense outlined />
          <v-simple-table dense>
            <tbody>
              <tr v-for="o in fix.candidates" :key="o.id">
                <td><v-checkbox v-model="fix.picked" :value="o.id" hide-details dense /></td>
                <td>{{ o.name }}<v-chip v-if="o.proposalStatus === 'pending'" x-small color="orange" dark class="ml-1">มีคำขอรอยืนยัน</v-chip></td><td>{{ o.upProvince }}</td><td class="text-no-wrap">{{ U.orderFigures(o, cfg).count }} ชิ้น · {{ cbm(U.orderFigures(o, cfg).volume) }} CBM</td>
                <td class="text-no-wrap">฿{{ money(o.orderValue) }}</td><td class="text-no-wrap">{{ U.weekLabel(o.earliestWeek) }}
                  <!-- Staff pick orders first come, first served: when each was booked. -->
                  <div v-if="o.createdAt" class="caption grey--text text--darken-1">{{ U.bookedLabel(o.createdAt) }}</div></td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="mt-2">รวม {{ fixTotals.count }} ชิ้น · ปริมาตร {{ cbm(fixTotals.volume) }} / {{ cbm(cfg.capacityCbm) }} CBM · ฿{{ money(fixTotals.value) }}</div>
          <!-- An empty list means nobody in these groups can receive that week --
               say so and name the earliest week, rather than "ยังไม่ได้เลือกออเดอร์". -->
          <v-alert v-if="!fix.candidates.length" dense type="info" class="mt-2">
            ยังไม่มีออเดอร์ที่ลูกค้าพร้อมรับภายในสัปดาห์ที่ออกรถ<template v-if="fixReadyWeek">
            — ลูกค้าพร้อมรับเร็วที่สุดตั้งแต่สัปดาห์ {{ U.weekLabel(fixReadyWeek) }} โปรดเลือกวันออกรถในสัปดาห์นั้นหรือหลังจากนั้น</template>
          </v-alert>
          <v-alert v-else-if="fixCheck && !fixCheck.ok" dense type="error" class="mt-2">{{ R.errorMessage(fixCheck.reason) }}</v-alert>
          <v-alert v-for="w in (fixCheck && fixCheck.warnings) || []" :key="w" dense type="warning" class="mt-2">
            {{ fixWarningText(w) }}</v-alert>
        </v-card-text>
        <v-card-actions><v-spacer />
          <v-btn text @click="fix.open = false">ยกเลิก</v-btn>
          <v-btn color="primary" :disabled="!fixCheck || !fixCheck.ok" :loading="fix.saving" @click="saveFix">จัดรอบ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <upcountry-trip-dialog v-model="tripDlg.open" :mode="tripDlg.mode" :trip="tripDlg.trip" :order="tripDlg.order"
      :orders="orders" :holds="tripDlg.trip ? (holds[tripDlg.trip.regNum] || []) : []" :cfg="cfg"
      :text="tripDlg.text" :saving="tripDlg.saving" @submit="onTripDialog" />
    <upcountry-plan-dialog v-model="planDlg.open" :trip="planTrip" :orders="planTrip ? ordersOf(planTrip) : []"
      :closed="closed" :plan-cfg="planCfg" :saving="planDlg.saving" :today="today" @submit="savePlan" />
    <upcountry-send-dialog v-model="sendOpen" :trip="sendTrip" :orders="sendTrip ? ordersOf(sendTrip) : []" :user="user" :before="sendBefore" @done="sendDone" />
    <upcountry-plan-settings v-model="planSettings" :cfg="planCfg" :user="user" @saved="c => { planCfg = c }" />

    <!-- Config dialog (administrator / admin) -->
    <v-dialog v-model="configDialog" max-width="480">
      <v-card>
        <v-card-title>ตั้งค่าเกณฑ์รถใหญ่</v-card-title>
        <v-card-text>
          <v-text-field v-model.number="cfgEdit.minValue" type="number" label="มูลค่าขั้นต่ำต่อรอบ (ก่อน VAT)" dense outlined />
          <v-text-field v-model.number="cfgEdit.standardValue" type="number" label="มูลค่ามาตรฐาน" dense outlined />
          <v-text-field v-model.number="cfgEdit.capacityCbm" type="number" label="ความจุรถ (CBM)" dense outlined />
          <v-text-field v-model.number="cfgEdit.fillFloor" type="number" label="สัดส่วนขั้นต่ำ (0-1)" dense outlined />
          <v-text-field v-model.number="cfgEdit.cbmPerItem" type="number" label="CBM ต่อชิ้น" dense outlined />
          <v-text-field v-model="cfgNote" label="หมายเหตุ (เช่น อนุมัติโดยกรรมการ วันที่…)" dense outlined />
        </v-card-text>
        <v-card-actions><v-spacer />
          <v-btn text @click="configDialog = false">ปิด</v-btn>
          <v-btn color="primary" :disabled="!cfgNote" @click="saveConfig">บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import mainMenu from '@/components/mainMenu'
import axios from 'axios'
import U from '@/components/upcountry'
import groupProvinces from '@/components/groupProvinces'
import upcountryTripDialog from '@/components/upcountryTripDialog'
import upcountryPlanDialog from '@/components/upcountryPlanDialog'
import shopClosedDays from '@/components/shopClosedDays'
import upcountryPlanSettings from '@/components/upcountryPlanSettings'
import P from '@/components/upcountryPlan'
import US from '@/components/upcountryService'
import S from '@/components/bookingService'
import R from '@/components/bookingRules'
import RJ from '@/components/returnJob'
import F from '@/components/fleet'
import { pushBookDateToSap, bookDateFailedNote } from '@/components/sapBookDate'
import upcountrySendDialog from '@/components/upcountrySendDialog'
import driverService from '@/components/driverService'
import D from '@/components/driverDay'
import sapJob from '@/components/sapJob'
import { reportError, reportWarning } from '@/components/report'
import { functions } from '@/firebase'

const DAY_KEYS = { 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu' }
const DAY_TH = { 1: 'จ.', 2: 'อ.', 3: 'พ.', 4: 'พฤ.' }

export default {
  name: 'upcountry',
  components: { mainMenu, groupProvinces, upcountryTripDialog, upcountryPlanDialog, shopClosedDays, upcountryPlanSettings, upcountrySendDialog },
  data () {
    return {
      // The send dialog's trip is read live from `trips` (the listener's fresh copy).
      // sendBefore: the plan last written to the sheet, when the send follows a
      // re-plan of a sent trip (planBefore, kept from before the save); null otherwise.
      sendOpen: false, sendTripId: null, sendBefore: null, planBefore: null,
      // Today's date, refreshed every minute and when the window regains focus: a
      // computed R.todayStr() has no reactive dependency, so Vue would cache it for
      // the page's life and a tab left open overnight would miss the departure-day lock.
      today: R.todayStr(), todayTimer: null,
      // tripId -> { key, ids: { orderId: true } }: the orders that have a row on a
      // pending sent trip's sheet. Only decides whether a rowless order past the
      // window is offered a take-off; the take-off looks the rows up again.
      sheetRows: {},
      // The order a take-off / ยกเลิกออเดอร์ is running for: one pass at a time.
      takingOff: '',
      U, US, R, P, cfg: U.CONFIG_DEFAULTS, orders: [], trips: [], holds: {}, largePlates: [],
      unsubs: [], loadError: [], configDialog: false, cfgEdit: {}, cfgNote: '',
      modeItems: [{ text: 'ยังไม่ทราบ', value: 'unknown' }, { text: 'ติดตั้ง', value: 'install' }, { text: 'ส่งอย่างเดียว', value: 'drop' }],
      // tripId -> true/false once a card is clicked; until then U.tripOpenByDefault decides.
      tripOpen: {},
      // The trip card's action dialog (upcountryTripDialog).
      tripDlg: { open: false, mode: 'add', trip: null, order: null, text: '', saving: false },
      // วางแผนเส้นทาง (upcountryPlanDialog); the trip is read live from `trips`.
      planDlg: { open: false, tripId: null, saving: false },
      // วันปิดร้าน rows (shopClosedDay) and the planner's settings.
      shopRows: [],
      planSettings: false,
      planCfg: P.normalizePlanConfig(null),
      // group key -> true while อัปเดตจาก SAP runs for it.
      refreshing: {},
      // group key -> true/false once clicked; until then U.groupOpenByDefault decides.
      groupOpen: {},
      fix: { open: false, groups: [], startDate: '', returnDate: '', regNum: '', candidates: [], picked: [], saving: false }
    }
  },
  computed: {
    role () { return this.$store.state.status },
    user () { return this.$store.state.user },
    webPath () { return this.$store.getters.webPath },
    isAdmin () { return ['administrator', 'admin'].indexOf(this.role) !== -1 },
    cards () { return U.boardCards(this.orders, this.cfg) },
    // Each waiting order once -- an overlap order sits on two cards.
    anyRefreshing () { return Object.keys(this.refreshing).some(k => this.refreshing[k]) },
    pooledCount () { return this.orders.filter(U.isPooled).length },
    groupItems () { return U.GROUPS.map(g => ({ text: g.label, value: g.key })) },
    closed () { return P.closedMap(this.shopRows) },
    planTrip () { return this.planDlg.tripId ? this.trips.find(t => t.id === this.planDlg.tripId) || null : null },
    sendTrip () { return this.sendTripId ? this.trips.find(t => t.id === this.sendTripId) || null : null },
    identities () { return this.$store.getters.identities },
    liveTrips () { return this.trips.filter(t => t.status === 'fixed' || t.status === 'sent') },
    tableDays () { return U.daysOf(this.fix.groups) },
    fixReadyWeek () { return U.firstReadyWeek(this.orders, this.fix.groups) },
    // Two weeks of Mon-Thu, from next Monday.
    stripDays () {
      const out = []
      const start = U.addDays(U.mondayOf(this.today), 7)
      for (let i = 0; i < 14; i++) {
        const date = U.addDays(start, i)
        const dow = i % 7 + 1
        if (!DAY_KEYS[dow]) continue
        const free = this.largePlates.filter(p => !U.holdsOverlap(this.holds[p] || [], date, date, null))
        out.push({ date, label: `${DAY_TH[dow]} ${date.slice(8)}/${date.slice(5, 7)}`, usual: this.cfg.departures[DAY_KEYS[dow]], free })
      }
      return out
    },
    freePlates () {
      if (!this.fix.startDate || !this.fix.returnDate) return this.largePlates
      return this.largePlates.filter(p => !U.holdsOverlap(this.holds[p] || [], U.loadingDay(this.fix.startDate), this.fix.returnDate, null))
    },
    pickedOrders () { return this.fix.candidates.filter(o => this.fix.picked.indexOf(o.id) !== -1) },
    fixTotals () { return U.totals(this.pickedOrders, this.cfg) },
    fixCheck () {
      if (!this.fix.open) return null
      return U.checkFix({ orders: this.pickedOrders, groups: this.fix.groups, startDate: this.fix.startDate,
        returnDate: this.fix.returnDate, regNum: this.fix.regNum, holds: this.holds[this.fix.regNum] || [],
        cfg: this.cfg, today: this.today })
    }
  },
  async created () {
    try { this.cfg = await US.getUpConfig() } catch (e) { this.fail('โหลดเกณฑ์ไม่สำเร็จ — ใช้ค่าเริ่มต้น', e) }
    try { this.planCfg = await US.getPlanConfig() } catch (e) { this.fail('โหลดค่าแผนเส้นทางไม่สำเร็จ — ใช้ค่าเริ่มต้น', e) }
    try { this.largePlates = (await F.loadFleet()).large || [] } catch (e) { this.fail('โหลดรายชื่อรถใหญ่ไม่สำเร็จ', e) }
    this.unsubs.push(US.subscribeOrders(rows => { this.orders = rows }, e => this.fail('โหลดออเดอร์ไม่สำเร็จ', e)))
    this.unsubs.push(US.subscribeOpenTrips(rows => { this.trips = rows }, e => this.fail('โหลดรอบรถไม่สำเร็จ', e)))
    this.unsubs.push(US.subscribeHolds(h => { this.holds = h }, e => this.fail('โหลดการจองรถไม่สำเร็จ', e)))
    this.unsubs.push(US.subscribeShopClosedDays(rows => { this.shopRows = rows }, e => this.fail('โหลดวันปิดร้านไม่สำเร็จ', e)))
  },
  mounted () {
    this.todayTimer = setInterval(this.refreshToday, 60000)
    window.addEventListener('focus', this.refreshToday)
  },
  beforeDestroy () {
    this.unsubs.forEach(u => u && u())
    clearInterval(this.todayTimer)
    window.removeEventListener('focus', this.refreshToday)
  },
  watch: {
    configDialog (open) { if (open) { this.cfgEdit = Object.assign({}, this.cfg); this.cfgNote = '' } },
    trips () { this.loadPendingSheets() }
  },
  methods: {
    fail (msg, e) {
      reportError('upcountry load', e)
      if (this.loadError.indexOf(msg) === -1) this.loadError.push(msg)
    },
    cbm (n) { return (Number(n) || 0).toFixed(2) },
    refOf (o) { return U.orderRef(o) },
    money (n) { return Number(n || 0).toLocaleString('th-TH') },
    pct (a, b) { return b ? Math.min(100, Math.round(a / b * 100)) : 0 },
    modeText (m) { return { install: 'ติดตั้ง', drop: 'ส่งอย่างเดียว' }[m] || 'ยังไม่ทราบ' },
    ordersOf (trip) { return U.tripOrderSort(this.orders.filter(o => o.upTripId === trip.id)) },
    // A fixed trip, or a sent one before its loading day is over, takes orders.
    editable (trip) { return U.checkAddable(trip, this.today).ok },
    sentEditable (trip) { return trip.status === 'sent' && U.checkSentEdit(trip, this.today).ok },
    // A sent trip whose orders changed since its plan was saved: re-plan it.
    sentReplan (trip) { return this.sentEditable(trip) && !P.planIsCurrent(trip) },
    refreshToday () { const d = R.todayStr(); if (d !== this.today) this.today = d },
    // Past the window, an order with NO row on the sheet -- added on the loading
    // day and never sent -- may still be taken off: dispatch can see nothing change.
    rowlessPastWindow (trip, o) {
      if (trip.status !== 'sent' || trip.sendState !== 'pending') return false
      const edit = U.checkSentEdit(trip, this.today)
      if (edit.ok || edit.reason !== 'UP_SENT_LOCKED') return false
      const known = this.sheetRows[trip.id]
      return !!known && !known.ids[o.id]
    },
    showTakeOff (trip, o) {
      if (trip.status === 'fixed') return true
      return (trip.orderIds || []).length > 1 && (this.sentEditable(trip) || this.rowlessPastWindow(trip, o))
    },
    // A sent trip's only order: a take-off is refused there (UP_LAST_ORDER).
    lastOnSent (trip) { return trip.status === 'sent' && !!trip.transNo && (trip.orderIds || []).length <= 1 },
    // Which orders have a row on each pending sent trip's sheet, re-read when the
    // trip's number or orders change, and after a send or a take-off (force).
    async loadPendingSheets (force) {
      for (const trip of this.trips) {
        if (trip.status !== 'sent' || !trip.transNo || trip.sendState !== 'pending') continue
        const key = trip.transNo + '|' + (trip.orderIds || []).slice().sort().join(',')
        if (!force && this.sheetRows[trip.id] && this.sheetRows[trip.id].key === key) continue
        try {
          const ids = {}
          const rows = await US.sendRowsOf(trip.transNo)
          rows.forEach(r => { if (US.onSheet(r, trip, trip.id) && r.sourceSlotId) ids[r.sourceSlotId] = true })
          this.$set(this.sheetRows, trip.id, { key, ids })
        } catch (e) { reportWarning('upcountry sheet rows', e) }
      }
    },
    hasCancelRequest (o) { return o.proposalStatus === 'pending' && !!o.proposal && o.proposal.kind === 'cancel' },
    isTripOpen (trip) {
      return trip.id in this.tripOpen ? this.tripOpen[trip.id] : U.tripOpenByDefault(trip, this.today)
    },
    toggleTrip (trip) { this.$set(this.tripOpen, trip.id, !this.isTripOpen(trip)) },
    setAllTrips (open) { this.liveTrips.forEach(t => this.$set(this.tripOpen, t.id, open)) },
    isGroupOpen (card) {
      return card.key in this.groupOpen ? this.groupOpen[card.key] : U.groupOpenByDefault(card)
    },
    toggleGroup (card) { this.$set(this.groupOpen, card.key, !this.isGroupOpen(card)) },
    setAllGroups (open) { this.cards.forEach(c => this.$set(this.groupOpen, c.key, open)) },
    async act (fn) {
      try { return await fn() } catch (e) {
        const why = U.actionMessage(e)
        reportError('upcountry action', e)
        await this.$root.$message('ทำรายการไม่สำเร็จ', [why], { type: 'alert', color: 'error', width: 420 })
      }
    },
    async sapDates (orders, cleared, pooled) {
      const failed = []
      for (const o of orders) failed.push(...await pushBookDateToSap(this.$store.getters.webPath, o))
      const note = bookDateFailedNote(failed, { cleared, pooled })
      if (note) await this.$root.$message('SAP', [note], { type: 'alert', color: 'warning', width: 420 })
    },
    openFix (groupKey) {
      // The first week anyone in the group can receive, never before next week.
      const start = U.defaultFixStart(this.orders, [groupKey], R.todayStr())
      this.fix = Object.assign(this.fix, { open: true, groups: [groupKey], startDate: start, regNum: '', picked: [] })
      this.refreshFix()
    },
    refreshFix () {
      this.fix.returnDate = this.fix.startDate ? U.defaultReturnDate(this.fix.startDate, this.fix.groups) : ''
      this.fix.candidates = this.fix.startDate ? U.eligible(this.orders, this.fix.groups, this.fix.startDate) : []
      // A pending request would refuse the whole fix: leave those out until decided.
      this.fix.picked = this.fix.candidates.filter(o => o.proposalStatus !== 'pending').map(o => o.id)
    },
    // BELOW_MIN does not stop จัดรอบ: delivery decides whether a trip under the
    // minimum goes (a sale event's display stock can fill a truck at ฿0).
    fixWarningText (w) {
      if (w === 'BELOW_MIN') return `มูลค่ารวม ฿${this.money(this.fixTotals.value)} ต่ำกว่าเกณฑ์ ฿${this.money(this.cfg.minValue)} — ฝ่ายจัดส่งยืนยันออกรอบได้`
      if (w === 'LOW_FILL') return 'ปริมาณต่ำกว่า ' + Math.round(this.cfg.fillFloor * 100) + '% ของรถ'
      return 'ปริมาณเกินความจุรถ'
    },
    async saveFix () {
      this.fix.saving = true
      const res = await this.act(() => US.fixTrip({ groups: this.fix.groups, startDate: this.fix.startDate,
        returnDate: this.fix.returnDate, regNum: this.fix.regNum, orderIds: this.fix.picked, user: this.user }))
      this.fix.saving = false
      if (!res) return
      this.fix.open = false
      await this.sapDates(this.pickedOrders.map(o => Object.assign({}, o, { date: this.fix.startDate })), false)
    },
    // The trip card's actions open upcountryTripDialog (they used window.prompt).
    openTripDialog (mode, trip, order, text) {
      this.tripDlg = { open: true, mode, trip, order: order || null, text: text || '', saving: false }
    },
    openAdd (trip) { this.openTripDialog('add', trip) },
    postpone (o) { this.openTripDialog('postpone', this.trips.find(t => t.id === o.upTripId) || null, o) },
    editReturn (trip) { this.openTripDialog('return', trip) },
    close (trip) { this.openTripDialog('close', trip) },
    // What the dialog collected; the writes and the SAP dates stay here.
    async onTripDialog (input) {
      const d = this.tripDlg
      d.saving = true
      let done = false
      try {
        if (d.mode === 'add') {
          const added = []
          for (const id of input.orderIds) {
            const ok = await this.act(() => US.addToTrip(d.trip.id, id, { user: this.user }).then(() => true))
            if (!ok) break
            added.push(this.orders.find(o => o.id === id))
          }
          if (added.length) await this.sapDates(added.filter(Boolean).map(o => Object.assign({}, o, { date: d.trip.startDate })), false)
          done = added.length === input.orderIds.length
          // A sent trip goes on: re-plan with the new orders, then send them to the sheet.
          if (d.trip.status === 'sent' && added.length) {
            d.open = false
            this.openPlan(d.trip)
            return
          }
        } else if (d.mode === 'postpone' && d.trip && d.trip.status === 'sent') {
          done = await this.takeOffSent(d.order, d.trip, 'postpone', input.week)
        } else if (d.mode === 'postpone') {
          const o = d.order
          done = !!await this.act(() => US.postponeOrder(o.id, { week: input.week, user: this.user, role: this.role }).then(() => true))
          // status 'cancelled' in this COPY only: it makes R.sapBookDate send '' and
          // clear SAP's date. The order itself is pooled again, not cancelled.
          if (done) await this.sapDates([Object.assign({}, o, { status: 'cancelled' })], true, true)
        } else if (d.mode === 'return') {
          done = !!await this.act(() => US.setReturnDate(d.trip.id, { returnDate: input.date, user: this.user }).then(() => true))
        } else if (d.mode === 'close') {
          done = !!await this.act(() => US.closeTrip(d.trip.id, { actualReturn: input.date, user: this.user }).then(() => true))
        }
      } finally {
        d.saving = false
      }
      if (done) d.open = false
    },
    // นำออกจากรอบ: back to the pool, the customer's week unchanged -- e.g. an
    // overlap order that belongs on the other group's truck.
    // One SO from SAP: its header (GetSo) and lines (GetProdList). null when SAP
    // returns nothing; throws when SAP cannot be reached.
    async readSapSo (soNo) {
      const base = `${this.$store.getters.webPath}deliveryWebService.asmx`
      const so = JSON.parse((await axios.post(`${base}/GetSo`, { so: soNo }, { timeout: 15000 })).data.d)
      const rows = JSON.parse((await axios.post(`${base}/GetProdList`, { so: soNo }, { timeout: 15000 })).data.d)
      return { so: Array.isArray(so) && so.length ? so[0] : null, rows: Array.isArray(rows) ? rows : [] }
    },
    // The RT an SO has gained since it was booked, as the fields to add to the
    // order -- or null (no RT, an unusable one, or SAP failing: the order's own
    // refresh must not fail because of it). An RT another live booking already
    // holds comes back as { taken } and is not added: one return, one booking,
    // the same guard bookingDate's เพิ่ม RT runs through fetchRt.
    // The SO's own U_KSK_ReturnNo first, else the newest open RT of its
    // ORIGINAL sale: GetRtBySo with the GetProdList rows' saleSo, never with
    // the SO's own number -- an RT points at the original sale, not the new SO.
    async readSapRt (so, prodRows, orderId) {
      try {
        const base = `${this.$store.getters.webPath}deliveryWebService.asmx`
        const post = async (ep, body) => JSON.parse((await axios.post(`${base}/${ep}`, body, { timeout: 15000 })).data.d)
        let bySo = []
        const saleSo = so && so.rtNo ? '' : RJ.saleSoOf(prodRows)
        if (saleSo) {
          const r = await post('GetRtBySo', { so: Number(saleSo) })
          bySo = Array.isArray(r) ? r : []
        }
        const pick = RJ.pickRt(so && so.rtNo, bySo)
        if (!pick) return null
        const got = await post('GetRT', { rtNo: Number(pick.rtNo) })
        const rt = Array.isArray(got) && got.length ? got[0] : null
        if (!rt || RJ.rtProblem(rt)) return null
        const taken = await S.findActiveBookingForRt(pick.rtNo)
        if (taken && taken.id !== orderId) return { taken: taken, rtNo: String(pick.rtNo) }
        const lines = await post('GetRtProdList', { docEntry: rt.docEntry })
        return RJ.rtFields(rt, Array.isArray(lines) ? lines : [], true)
      } catch (e) {
        reportWarning('upcountry readSapRt', e)
        return null
      }
    },
    // อัปเดตจาก SAP: each waiting order of the group, one at a time (a handful of
    // SOs; SAP is not hammered in parallel). U.sapRefresh decides; a SAP state
    // that should stop an order is reported, never acted on -- except that an
    // order already on a ใบจัดรถ is offered to นำออกจากคิว, which staff confirm.
    async refreshGroup (card) {
      this.$set(this.refreshing, card.key, true)
      let updated = 0
      const problems = []
      // Orders SAP already holds on a ใบจัดรถ (keyed by hand): offered below.
      const dispatched = []
      try {
        for (const o of card.pool) {
          // A pickup has no SO to read or update.
          if (RJ.isReturnOnly(o)) continue
          try {
            const { so, rows } = await this.readSapSo(o.soNo)
            const rtRead = so && !o.rtNo ? await this.readSapRt(so, rows, o.id) : null
            const rtPatch = rtRead && rtRead.taken ? null : rtRead
            if (rtRead && rtRead.taken) {
              problems.push(`${U.handoverRef(o)} (${o.name}): RT ${rtRead.rtNo} มีการจองอยู่แล้ว (${RJ.refText(rtRead.taken)}) — ไม่ได้เพิ่มในออเดอร์นี้`)
            }
            const r = U.sapRefresh(o, so, rows, rtPatch)
            if (r.changed) {
              await US.refreshFromSap(o.id, { patch: r.patch, from: r.from, user: this.user, role: this.role })
              updated++
            }
            if (r.problem) problems.push(`${U.handoverRef(o)} (${o.name}): ${r.problem}`)
            if (r.dispatched) dispatched.push({ o, transNo: r.dispatched })
          } catch (e) {
            reportError('upcountry refreshGroup', e)
            problems.push(`${U.handoverRef(o)} (${o.name}): ${e && e.reason ? U.actionMessage(e) : 'อ่านข้อมูลจาก SAP ไม่ได้'}`)
          }
        }
      } finally {
        this.$set(this.refreshing, card.key, false)
      }
      await this.$root.$message('อัปเดตจาก SAP', [
        `${card.label}: ตรวจ ${card.pool.length} ออเดอร์ · ข้อมูลเปลี่ยน ${updated} ออเดอร์`
      ].concat(problems.length ? ['', 'โปรดตรวจสอบ:'].concat(problems) : []),
      { type: 'alert', color: problems.length ? 'warning' : 'success', width: 520 })
      if (dispatched.length) await this.retireDispatched(dispatched)
    },
    // นำออกจากคิว: orders SAP already holds on a hand-keyed ใบจัดรถ leave the
    // pool as cancelled, only when staff confirm. The sheet and SAP are untouched.
    async retireDispatched (list) {
      const ok = await this.$root.$message(`อยู่ในใบจัดรถแล้ว (คีย์เองในจัดรถ) — ${list.length} ออเดอร์`,
        list.map(d => `${U.orderRef(d.o)} · ${d.o.name} · ใบจัดรถ ${d.transNo}`).concat(['',
          `กด ยืนยัน เพื่อนำออกจากคิว ${list.length} ออเดอร์ — ยกเลิกออเดอร์ในหน้านี้ โดยไม่แก้ใบจัดรถและ SAP`]),
        { type: 'confirm', color: 'warning', width: 560 })
      if (!ok) return
      let done = 0
      const failed = []
      for (const d of list) {
        try {
          await US.retireDispatched(d.o.id, { transNo: d.transNo, user: this.user, role: this.role })
          done++
        } catch (e) {
          reportError('upcountry retireDispatched ' + d.o.soNo, e)
          failed.push(`${U.orderRef(d.o)} · ${d.o.name}: ${U.actionMessage(e)}`)
        }
      }
      await this.$root.$message('นำออกจากคิว', [`นำออกจากคิวแล้ว ${done} ออเดอร์`].concat(failed.length ? ['', 'ไม่ได้นำออก:'].concat(failed) : []),
        { type: 'alert', color: failed.length ? 'warning' : 'success', width: 520 })
    },
    async removeFromTrip (o, trip) {
      const ok = await this.$root.$message('นำออกจากรอบ', [
        `${this.refOf(o)} · ${o.name}`,
        `จะกลับไปรอจัดรอบ (ลูกค้าพร้อมรับตั้งแต่สัปดาห์ ${U.weekLabel(o.earliestWeek)} เหมือนเดิม)`,
        (o.upGroup || []).length === 1 && U.groupsOf(o.upProvince).length > 1
          ? `จ.${o.upProvince} อยู่ 2 กลุ่ม — จะกลับไปแสดงทั้ง 2 กลุ่ม` : '',
        `รอบ ${trip.startDate} จะเหลือ ฿${this.money(trip.value - (Number(o.orderValue) || 0))}`
      ].filter(Boolean), { type: 'confirm', color: 'warning', width: 460 })
      if (!ok) return
      const done = await this.act(() => US.removeFromTrip(o.id, { user: this.user, role: this.role }).then(() => true))
      // Same as postpone: the copy's 'cancelled' status only clears SAP's date.
      if (done) await this.sapDates([Object.assign({}, o, { status: 'cancelled' })], true, true)
    },
    // ยกเลิกออเดอร์ on a fixed trip: nothing is on a sheet yet.
    async cancelOrder (o) {
      if (this.takingOff) return
      this.takingOff = o.id
      try { await this.cancelOrderPass(o) } finally { this.takingOff = '' }
    },
    async cancelOrderPass (o) {
      const ok = await this.$root.$message('ยกเลิกออเดอร์', [`${this.refOf(o)} · ${o.name}`], { type: 'confirm', color: 'warning', width: 420 })
      if (!ok) return
      const done = await this.act(() => US.cancelUpOrder(o.id, { user: this.user, reason: 'ยกเลิกโดยฝ่ายจัดส่ง', role: this.role, identities: this.identities }).then(() => true))
      if (done) await this.sapDates([Object.assign({}, o, { status: 'cancelled' })], true)
    },
    // An order off a SENT trip (นำออกจากรอบ 'pool', เลื่อนไปรอบถัดไป 'postpone',
    // ยกเลิกออเดอร์ 'cancel'). Its rows on the sheet are deleted, so it warns
    // about stamped work like ยกเลิกการส่ง; then SAP (CancelSo / CancelRt, only on
    // a document that holds this trip's number), then the transaction, then the
    // book date. A SAP failure stops before anything is written. true when done.
    async takeOffSent (o, trip, mode, week) {
      if (this.takingOff) return false
      this.takingOff = o.id
      try { return await this.takeOffSentPass(o, trip, mode, week) } finally { this.takingOff = '' }
    },
    // What the transaction would refuse and the view can tell beforehand, asked
    // BEFORE SAP is touched: a refusal after CancelSo would leave SAP cancelled
    // and the order still on the sheet. '' when nothing stands in the way.
    // `rowless`: the lookup found no row of this order on the sheet, which lets it
    // leave after the window (UP_SENT_LOCKED), as takeOffSentTrip allows.
    takeOffRefusal (o, trip, mode, week, rowless) {
      const today = R.todayStr()
      const edit = U.checkSentEdit(trip, today)
      if (!edit.ok && !(rowless && edit.reason === 'UP_SENT_LOCKED')) return edit.reason
      if ((trip.orderIds || []).length <= 1) return 'UP_LAST_ORDER'
      if (o.proposalStatus === 'pending' && !(mode === 'cancel' && o.proposal && o.proposal.kind === 'cancel')) return 'PROPOSAL_PENDING'
      if (mode === 'postpone') {
        const c = U.checkUpOrder(Object.assign({}, o, { earliestWeek: week }), today)
        if (!c.ok) return c.reason
      }
      return ''
    },
    async takeOffSentPass (o, trip, mode, week) {
      const ref = this.refOf(o)
      try {
        const rows = trip.transNo
          ? (await US.sendRowsOf(trip.transNo)).filter(r => US.onSheet(r, trip, trip.id) && r.sourceSlotId === o.id)
          : []
        // Asked after the row lookup (a rowless order may leave past the window)
        // and still before SAP is touched.
        const refusal = this.takeOffRefusal(o, trip, mode, week, !rows.length)
        if (refusal) {
          await this.$root.$message('ทำรายการไม่สำเร็จ', [U.actionMessage({ reason: refusal })], { type: 'alert', color: 'error', width: 420 })
          return false
        }
        const notes = []
        let finished = false
        for (const r of rows) {
          const guard = await driverService.stampGuardForListDoc(r.id)
          const note = D.deleteGuardNote(guard)
          if (note) notes.push((r.soNo || r.rtNo) + ': ' + note)
          if (guard && guard.finished) finished = true
        }
        const title = { pool: 'นำออกจากรอบ', postpone: 'เลื่อนไปรอบถัดไป', cancel: 'ยกเลิกออเดอร์' }[mode]
        const ok = await this.$root.$message(title,
          [`${ref} · ${o.name}`, rows.length ? `ลบออกจากใบจัดรถ ${trip.transNo} และยกเลิกใน SAP` : 'ยังไม่อยู่ในใบจัดรถ — นำออกจากรอบได้เลย'].concat(notes),
          { type: 'confirm', color: finished ? 'error' : 'warning', width: 480 })
        if (!ok) return false
      } catch (e) {
        reportError('take off sent: lookup ' + ref, e)
        await this.$root.$message('ทำรายการไม่สำเร็จ', [U.actionMessage(e)], { type: 'alert', color: 'error', width: 420 })
        return false
      }
      let sapCancelled = false
      try {
        if (o.soNo && P.stampedWith(await sapJob.readSo(this.webPath, o.soNo), trip.transNo)) {
          await sapJob.cancelSo(this.webPath, o.soNo)
          sapCancelled = true
        }
        if (o.rtNo && (RJ.jobTypeOf(o) === 'return' || RJ.jobTypeOf(o) === 'exchange')) {
          const r = await sapJob.readRt(this.webPath, o.rtNo)
          if (r && P.stampedWith(r.rt, trip.transNo)) { await sapJob.cancelRt(this.webPath, o.rtNo); sapCancelled = true }
        }
      } catch (e) {
        reportError('take off sent: SAP cancel ' + ref, e)
        await this.$root.$message('ทำรายการไม่สำเร็จ', [`ยกเลิกใน SAP ไม่สำเร็จ: ${ref} — ยังไม่ได้นำออกจากรอบ`], { type: 'alert', color: 'error', width: 420 })
        return false
      }
      try {
        await US.takeOffSentTrip(o.id, { mode, week, reason: 'ยกเลิกโดยฝ่ายจัดส่ง', user: this.user, role: this.role })
      } catch (e) {
        reportError('take off sent: ' + ref, e)
        // SAP no longer holds this trip's number while the sheet still lists the order: say so.
        const lines = sapCancelled
          ? ['ยกเลิกใน SAP แล้ว แต่ยังไม่ได้นำออกจากรอบ — โปรดลองอีกครั้ง หรือแก้ใน SAP', U.actionMessage(e)]
          : [U.actionMessage(e)]
        await this.$root.$message('ทำรายการไม่สำเร็จ', lines, { type: 'alert', color: 'error', width: 460 })
        return false
      }
      // A send left pending only for this order is complete now when every order
      // still on the trip has its row. finishSend WITHOUT sheetPlan: the take-off
      // already took the order out of it.
      if (trip.sendState === 'pending') await this.finishIfAllSent(trip, o.id)
      // The copy's 'cancelled' status clears SAP's date; pooled again unless it was cancelled.
      await this.sapDates([Object.assign({}, o, { status: 'cancelled' })], true, mode !== 'cancel')
      return true
    },
    async finishIfAllSent (trip, goneId) {
      try {
        const cur = this.trips.find(t => t.id === trip.id) || trip
        const left = (cur.orderIds || []).filter(id => id !== goneId)
        const rows = (await US.sendRowsOf(trip.transNo)).filter(r => US.onSheet(r, trip, trip.id))
        if (left.every(id => rows.some(r => r.sourceSlotId === id))) await US.finishSend(trip.id)
      } catch (e) { reportError('take off sent: finish send', e) }
    },
    async cancelTrip (trip) {
      const ok = await this.$root.$message('ยกเลิกรอบ', ['ออเดอร์ทั้งหมดจะกลับไปรอจัดรอบ'], { type: 'confirm', color: 'warning', width: 420 })
      if (!ok) return
      const res = await this.act(() => US.cancelTrip(trip.id, { user: this.user }))
      // Same trick as postpone: clears SAP's date; the orders are pooled again.
      if (res) await this.sapDates(res.orders.map(o => Object.assign({}, o, { status: 'cancelled' })), true, true)
    },
    openSend (trip, before) { this.sendTripId = trip.id; this.sendBefore = before || null; this.sendOpen = true },
    sendDone ({ partial }) {
      // The open-trips listener redraws the card; the dialog stays open on a
      // partial send so ลองอีกครั้ง is one tap away.
      if (!partial) this.sendOpen = false
      this.loadPendingSheets(true)
    },
    // ยกเลิกการส่ง -- the SIXTH deliveryList delete site, so it warns about
    // anything a driver already stamped (stampGuardForListDoc) and never refuses.
    // SAP first (CancelSo / CancelRt), then the rows and timelines, then the trip.
    async unsendTrip (trip) {
      try {
        const rows = (await US.sendRowsOf(trip.transNo)).filter(r => US.onSheet(r, trip, trip.id))
        const notes = []
        let finished = false
        for (const r of rows) {
          const guard = await driverService.stampGuardForListDoc(r.id)
          const note = D.deleteGuardNote(guard)
          if (note) notes.push((r.soNo || r.rtNo) + ': ' + note)
          if (guard && guard.finished) finished = true
        }
        // A partial send can leave orders stamped in SAP with no row on the sheet.
        const rowless = trip.sendState === 'pending'
          ? this.ordersOf(trip).filter(o => !rows.some(r => r.sourceSlotId === o.id))
          : []
        if (rowless.length) notes.push(`ออเดอร์ที่ยังไม่มีรายการจัดส่ง ${rowless.length} รายการ จะถูกยกเลิกใน SAP ด้วย ถ้า SAP บันทึกเลขที่ใบจัดรถนี้ไว้แล้ว`)
        const ok = await this.$root.$message('ยกเลิกการส่งเข้าจัดรถ',
          [`ลบใบจัดรถ ${trip.transNo} (${rows.length} รายการ) และยกเลิกใน SAP`].concat(notes),
          { type: 'confirm', color: finished ? 'error' : 'warning', width: 480 })
        if (!ok) return
        const sapFailed = []
        for (const r of rows) {
          try {
            if (r.soNo) await sapJob.cancelSo(this.webPath, r.soNo)
            if (r.rtNo && (r.status === 'RT' || r.rtDocEntry)) await sapJob.cancelRt(this.webPath, r.rtNo)
          } catch (e) { reportError('unsend: SAP cancel ' + (r.soNo || r.rtNo), e); sapFailed.push(r.soNo || r.rtNo) }
        }
        // An order with no row may or may not have reached UpdateSo / UpdateRT,
        // and CancelSo / CancelRt clear whatever number is there -- another
        // sheet's, or the one an RT inherits from its original sale. So each is
        // read first and cancelled only when SAP holds THIS trip's number.
        for (const o of rowless) {
          const ref = o.soNo || o.rtNo
          try {
            if (o.soNo && P.stampedWith(await sapJob.readSo(this.webPath, o.soNo), trip.transNo)) {
              await sapJob.cancelSo(this.webPath, o.soNo)
            }
            if (o.rtNo && (RJ.jobTypeOf(o) === 'return' || RJ.jobTypeOf(o) === 'exchange')) {
              const r = await sapJob.readRt(this.webPath, o.rtNo)
              if (r && P.stampedWith(r.rt, trip.transNo)) await sapJob.cancelRt(this.webPath, o.rtNo)
            }
          } catch (e) { reportError('unsend: SAP cancel (no row) ' + ref, e); sapFailed.push(ref) }
        }
        await US.unsend(trip.id, { user: this.user })
        if (sapFailed.length) {
          await this.$root.$message('ยกเลิกการส่งแล้ว แต่ SAP ยังไม่ถูกยกเลิก',
            ['โปรดยกเลิกใน SAP: ' + sapFailed.join(', ')], { type: 'alert', color: 'error', width: 420 })
        }
      } catch (e) {
        reportError('unsend trip', e)
        await this.$root.$message('ยกเลิกการส่งไม่สำเร็จ', [U.actionMessage(e)], { type: 'alert', color: 'error', width: 420 })
      }
    },
    // planBefore: what the sheet was last written with, taken before the save
    // (savePlan leaves sheetPlan alone; finishSend sets it after the send).
    openPlan (trip) {
      this.planBefore = trip.sheetPlan || null
      this.planDlg = { open: true, tripId: trip.id, saving: false }
    },
    // บันทึกแผน: then each order whose day moved is written to SAP, as every
    // other up-country date change is. A sent trip then goes to the send dialog,
    // which writes new orders onto the sheet and moves the rows the plan moved.
    async savePlan ({ plan, useReturn }) {
      const tripId = this.planDlg.tripId
      const sent = !!(this.planTrip && this.planTrip.status === 'sent')
      this.planDlg.saving = true
      const res = await this.act(() => US.savePlan({ tripId, plan, useReturn, user: this.user }))
      this.planDlg.saving = false
      if (!res) return
      this.planDlg.open = false
      if (res.changed.length) await this.sapDates(res.changed, false)
      if (!sent) return
      if (res.returnDate) await this.act(() => US.setSheetTripEnd(tripId))
      const trip = this.trips.find(t => t.id === tripId)
      if (trip) this.openSend(trip, this.planBefore)
    },
    // ยกเลิกแผน: every order back on the trip's start date, then SAP, as บันทึกแผน.
    async clearPlan (trip) {
      const moved = this.ordersOf(trip).filter(o => o.date && o.date !== trip.startDate).length
      const ok = await this.$root.$message('ยกเลิกแผนเส้นทาง', [
        `รอบ ${trip.startDate} รถ ${trip.regNum}`,
        moved ? `ออเดอร์ ${moved} รายการจะกลับไปส่งวันออกรถ ${trip.startDate} และแก้วันส่งใน SAP` : 'ทุกออเดอร์ส่งวันออกรถอยู่แล้ว',
        'วันกลับไม่เปลี่ยน — แก้ได้ที่ แก้วันกลับ'
      ], { type: 'confirm', color: 'warning', width: 460 })
      if (!ok) return
      const res = await this.act(() => US.clearPlan({ tripId: trip.id, user: this.user }))
      if (res && res.changed.length) await this.sapDates(res.changed, false)
    },
    // With a current plan: day, date, stop number and ETA first, in driving
    // order (P.handoverRows). Without one: the province sort, as before.
    async copyHandover (trip) {
      const planned = P.handoverRows(trip, this.ordersOf(trip))
      const cells = o => [U.handoverRef(o), o.name, o.telephone, o.upProvince, U.orderFigures(o, this.cfg).count, this.cbm(U.orderFigures(o, this.cfg).volume), this.modeText(o.installMode)]
      const head = 'SO / RT\tลูกค้า\tโทร\tจังหวัด\tชิ้น\tCBM\tติดตั้ง'
      const rows = planned
        ? planned.map(r => [r.day, r.date, r.seq, r.arrive].concat(cells(r.order)).join('\t'))
        : this.ordersOf(trip).map(o => cells(o).join('\t'))
      const text = [`รอบ ${trip.startDate} รถ ${trip.regNum}`, (planned ? 'วัน\tวันที่\tลำดับ\tเวลาถึง\t' : '') + head].concat(rows).join('\n')
      try { await navigator.clipboard.writeText(text); this.$root.$message('คัดลอกแล้ว', ['วางในจัดรถได้เลย'], { type: 'alert', width: 360 }) } catch (e) { this.openTripDialog('copy', trip, null, text) }
    },
    async saveConfig () {
      const changes = {}
      ;['minValue', 'standardValue', 'capacityCbm', 'fillFloor', 'cbmPerItem'].forEach(k => {
        if (Number(this.cfgEdit[k]) !== this.cfg[k]) changes[k] = Number(this.cfgEdit[k])
      })
      const done = await this.act(() => functions.httpsCallable('setUpcountryConfig')({ changes, note: this.cfgNote }).then(() => true))
      if (done) {
        try { this.cfg = await US.getUpConfig() } catch (e) { this.fail('โหลดเกณฑ์ไม่สำเร็จ — บันทึกแล้วแต่ยังแสดงค่าเดิม', e) }
        this.configDialog = false
      }
    }
  }
}
</script>

<style scoped>
.trip-head {
  cursor: pointer;
  user-select: none;
}
/* The trip table's text is 14px (0.875rem). An input's own 16px and its
   top/bottom padding made the choice sit big and below the rest of the row. */
.install-mode {
  margin-top: 0;
  padding-top: 0;
  flex: 0 0 auto;
}
.install-mode ::v-deep .v-select__selection,
.install-mode ::v-deep .v-select__selections input {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.install-mode ::v-deep .v-select__selection--comma {
  margin: 0;
}
.install-mode ::v-deep .v-select__selections input {
  padding: 0;
}
.install-mode ::v-deep .v-input__slot {
  min-height: 0;
}
.install-mode ::v-deep .v-input__append-inner {
  margin-top: 0;
}
/* A column that fills the v-col (which v-row stretches to the row's tallest),
   so the cards of a row match and mt-auto can push จัดรอบ to the bottom. */
.group-card {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.pool-row {
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.pool-row:last-child {
  border-bottom: none;
}
/* An item of a pool row's detail line is never split; the line breaks between them. */
.pool-item {
  white-space: nowrap;
}
/* One line; a very long name is cut with "…" and shown whole on hover (title). */
.pool-name {
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
